import { Controller } from "stimulus";

// edit-profile (Stimulus controller)
//
// Persiste les critères saisis dans le formulaire « Profil client » et dans le
// filtre catalogue à chaque modification d'un champ (debounced). La requête est
// POSTée vers `update-url` (Saas::ToolsController#update_criterias) qui sauvegarde
// sur le profil actif (connecté) ou en session (anonyme).
//
// Cette persistance s'AJOUTE aux appels `api-sync` (calcul) : les deux mécanismes
// écoutent les évènements `input` indépendamment et ne s'interfèrent pas.
export default class extends Controller {
    static values = { updateUrl: String };

    connect() {
        this.debounceTimer = null;
        // Mémorise une signature déterministe du dernier payload réellement
        // posté, pour dédupliquer les `update_criterias` identiques (#395).
        this.lastSentBody = null;
    }

    disconnect() {
        if (this.debounceTimer) clearTimeout(this.debounceTimer);
    }

    submit(event) {
        // #395 : ignorer les évènements synthétiques émis par api-sync lors de
        // la réapplication des valeurs calculées (« Montant de l'impôt », …) ;
        // ils ne correspondent à aucune saisie utilisateur et ne doivent pas
        // déclencher de persistance. Même garde que api_sync#handleAnyInput.
        if (this.isFromInvestApply(event)) return;
        this.post();
    }

    debouncedSubmit(event) {
        // #395 : voir submit() — on filtre aussi côté debounce pour ne pas
        // armer un timer qui re-posterait un payload inchangé.
        if (this.isFromInvestApply(event)) return;
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => this.post(), 300);
    }

    // Détecte les évènements `input`/`change` synthétiques dispatché par
    // api_sync_controller#applyInputSuggestions (marqués `__investDetailsFromApply`
    // / `detail.fromInvestApply`).
    isFromInvestApply(event) {
        return Boolean(
            event && (event.__investDetailsFromApply || (event.detail && event.detail.fromInvestApply))
        );
    }

    post() {
        const url = this.hasUpdateUrlValue ? this.updateUrlValue : null;
        if (!url) return;

        const formData = new FormData(this.element);

        // #425 : les groupes de cases à cocher « tout = pas de filtre »
        // (Orientation / Étage) ne renvoient RIEN dans le FormData quand toutes
        // les cases sont décochées. Sans intervention, l'auto-save ne réécrit
        // donc jamais `invest_expositions` / `invest_floors` et les anciennes
        // valeurs filtrées restent en base (Est / RDC réapparaissent au reload).
        // On réinjecte ici toutes les valeurs possibles du groupe (= « pas de
        // filtre ») pour que la mutation persiste bien l'état déselectionné.
        this.fillEmptyAllOrNoneGroups(formData);

        // Défense en profondeur (#346) : le profil principal ne doit jamais
        // soumettre `criterias[label]`. Le champ « Nom du profil client » est
        // seulement masqué (`d-none`) pour le principal mais reste dans le DOM
        // ; une valeur stale y persisterait et écraserait le nom de l'user. On
        // retire la clé du payload quand le profil actif est principal.
        // (À faire AVANT le calcul de la signature de dédup pour que celle-ci
        // porte sur le payload réellement envoyé.)
        if (this.isPrincipalProfile()) formData.delete("criterias[label]");

        // #395 : dédup — on ne POST que si le payload persisté a réellement
        // changé depuis le dernier envoi appliqué. Évite les écritures
        // redondantes (DB/session) et les courses dues aux cycles api-sync.
        const body = this.serializeFormData(formData);
        if (body === this.lastSentBody) return;
        this.lastSentBody = body;

        const headers = { Accept: "application/json" };
        const csrf = document.querySelector('meta[name="csrf-token"]');
        if (csrf) headers["X-CSRF-Token"] = csrf.getAttribute("content");

        fetch(url, {
            method: "POST",
            headers,
            body: formData,
            credentials: "same-origin",
        }).catch((err) => {
            // En cas d'échec réseau, on réautorise un retry du même payload
            // (comme api-sync remet `lastRequestBody = null` dans son catch).
            this.lastSentBody = null;
            // eslint-disable-next-line no-console
            console.warn("edit-profile persist error:", err);
        });
    }

    // #425 : pour chaque groupe de cases à cocher « tout = pas de filtre »
    // présent dans le formulaire, si AUCUNE case n'est cochée, on ajoute au
    // FormData toutes les valeurs possibles du groupe. Les valeurs sont dérivées
    // du DOM (et non codées en dur) pour rester symétriques avec le rendu
    // serveur (`expositions_select_options` / `floors_select_options`).
    fillEmptyAllOrNoneGroups(formData) {
        const groupNames = ["criterias[invest_expositions][]", "criterias[invest_floors][]"];

        groupNames.forEach((name) => {
            const boxes = Array.from(
                this.element.querySelectorAll(`input[type="checkbox"][name="${name}"]`)
            );
            if (boxes.length === 0) return;
            if (boxes.some((box) => box.checked)) return;

            boxes.forEach((box) => formData.append(name, box.value));
        });
    }

    // Sérialise un FormData de façon déterministe (tri des entrées [name, value])
    // pour comparer deux payloads indépendamment de l'ordre d'itération.
    serializeFormData(formData) {
        const entries = [];
        for (const [name, value] of formData.entries()) {
            // On ignore les fichiers : le formulaire de critères n'en contient
            // pas, et leur sérialisation n'est pas déterministe.
            if (typeof value !== "string") continue;
            entries.push([name, value]);
        }
        entries.sort((a, b) => (a[0] === b[0] ? (a[1] < b[1] ? -1 : a[1] > b[1] ? 1 : 0) : a[0] < b[0] ? -1 : 1));
        return JSON.stringify(entries);
    }

    // Le caractère principal du profil actif est porté par le bloc « Nom du
    // profil client » (`data-profile-name` / `data-profile-principal`), mis à
    // jour côté serveur au rendu et côté client lors d'un changement de profil
    // sans reload (cf. profiles_controller#updateProfileNameSection).
    isPrincipalProfile() {
        const section = this.element.querySelector("[data-profile-name]") ||
            document.querySelector("[data-profile-name]");
        return Boolean(section) && section.getAttribute("data-profile-principal") === "true";
    }
}
